import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";

// for validation
import { useForm, FormProvider, useFormContext } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { productSchema } from "@/validationSchemas/productSchema";

// import utils or hooks
import useProductFormLoader from "../hooks/useProductFormLoader";
import useSaveProduct from "../hooks/useSaveProduct";
import useSaveBarStore from "@/stores/useSaveBarStore";
import { showSuccess, showError } from "@/utils/toast";
import { generateVariantCombos } from "../utils/generateVariantCombos";

// imports the sections of the form
import BasicInfoSection from "./BasicInfoSection";
import PricingSection from "./PricingSelection";
import InventorySection from "./InventorySection";
import VariationSection from "./VariationSection";

// Import inhouse icons
import { AlertCircle } from "@/components/icons_new";

// Import reusable components
import { Spinner } from "@/components/ui/Spinner";
import EmptyStateCard from "@/components/ui/EmptyStateCard";
import { useStorePath } from "@hooks/useStorePath";

const ProductPageForm = ({ productId }) => {
  const navigate = useNavigate();
  const { storePath } = useStorePath();

  const isEditMode = Boolean(productId);
  const variantRef = useRef(null);
  const isHydrating = useRef(true);

  const [loading, setLoading] = useState(isEditMode);
  const [error, setError] = useState(null);

  // TODO: Send data to backend, save
  const { saveProduct, error: saveError } = useSaveProduct({
    isEditMode,
    productId,
  });

  // fetch and normalize product data when editing
  const { 
    data: productData, 
    isLoading: isFetching, 
    error: fetchError 
  } = useProductFormLoader(productId);

  // Initialize React Hook Form and its functions
  const form = useForm({
    resolver: zodResolver(productSchema),
    defaultValues: {
      productName: "",
      productDescription: "",
      productImages: [],
      productCategory: "",
      productStatus: "active",
      productPrice: undefined,
      productDiscountedPrice: undefined,
      productCostPerItem: undefined,
      trackInventory: true,
      inventoryQuantity: 0,
      productSku: "",
      productBarcode: "",
      hasSKUBarcode: false,
      variants: [],
    },
  });

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors, isDirty },
  } = form;

  // Set up contextual save bar
  const setSaveBarState = useSaveBarStore((state) => state.setState);
  const hideSaveBar = useSaveBarStore((state) => state.hideSaveBar);

  function normalizeImages(images = []) {
    return images.map((img, index) => ({
      id: img.id ?? undefined, // backend asset id (may be undefined for new uploads)
      url: img.url,
      alt: img.alt,
      role: index === 0 ? "primary" : "secondary",
      position: index,
    }));
  }

  // TODO: Involve both saving of a new product and an edit on an existing product
  // Handle the form submition logic
  const onSubmit = useCallback(async (data) => {
    if (variantRef.current?.hasUnfinishedVariants?.()) {
      showError("Please finish editing all variant options before saving.");
      return;
    }

    try {
      const grouped = data.variants;
      const variantMap = Object.fromEntries(
        (grouped || []).map(opt => [opt.variantOption, opt.variantValues])
      );
      const comboVariants = generateVariantCombos(variantMap);

      const normalizedImages = normalizeImages(data.productImages);

      const finalData = {
        ...data,
        productImages: normalizedImages,
        // Preserve the  option names and their respective values alongside generated combos
        options: data.variants,
        variants: comboVariants,
      };
      
      const saved = await saveProduct(finalData);
      console.log("saved: ", saved);
      showSuccess("Product Saved!")
      hideSaveBar();
      reset(saved);

      // Redirect to the products page(products table)
      navigate(storePath("products"))
    } catch (err) {
      console.error("Save Product Failed:", err);
      showError("Failed to save", err.message || "Something went wrong.")
    }
  }, [saveProduct, hideSaveBar, reset, navigate]);

  useEffect(() => {
    const subscription = watch((value, { name, type }) => {
      if (name === "variants") {
        setSaveBarState((prev) => ({
          ...prev,
          isVisible: true,
        }));
      }
    });

    return () => subscription.unsubscribe();
  }, [watch, setSaveBarState]);

  // Sync the contextual save bar with the form state(isDirty == unsaved changes present)
  useEffect(() => {
    setSaveBarState({
      isVisible: isDirty,
      onSave: handleSubmit(onSubmit),
      onDiscard: () => reset(),
    });
  }, [isDirty, handleSubmit, onSubmit, reset, setSaveBarState]);

  // preload form when in edit mode and product data is available
  useEffect(() => {
    if (isEditMode && productData) {
      reset({
        ...productData,
        productCategory: productData.productCategory?.toLowerCase?.(),
      });
      queueMicrotask(() => {
        isHydrating.current = false;
      });
      setLoading(false);
    } else if (fetchError) {
      setError(fetchError);
      setLoading(false);
    }
  }, [isEditMode, productData, fetchError, reset]);

  // When creating a new product, exit hydration after the first render so form fields stay dirty
  useEffect(() => {
    if (!isEditMode) {
      queueMicrotask(() => {
        isHydrating.current = false;
      });
    }
  }, [isEditMode]);

  // prevent tab close or browser refresh if form has unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
  
    window.addEventListener("beforeunload", handleBeforeUnload);
  
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isDirty]);

  // Cleanup savebar state on component unmount(onSave and onDiscard)
  useEffect(() => {
    return () => {
      useSaveBarStore.setState({
        isVisible: false,
        onSave: null,
        onDiscard: null,
      });
    };
  }, []);

  // TODO: Implement the 'Unsaved Changes' logic
  // block navigation if there's unsaved changes
  // const {
  //   showPrompt,
  //   confirmNavigation,
  //   cancelNavigation
  // } = useUnsavedChangesPrompt(isDirty);

  // Loading fallback
  if (loading || isFetching) {
    return (
      <Spinner size="medium">
        <p className="text-sm text-gray-500 mt-2">Loading product data</p>
      </Spinner>
    );
  };

  // Error fallabck
  if (error) {
    return (
      <EmptyStateCard
        icon={<AlertCircle className="h-6 w-6 text-red-500"/>}
        title="Failed to load product data"
        description={`We couldn't load this product. ${error?.message || ""}`}
        buttonLabel="Retry"
        // TODO: +Use refetch from react query
      />
    );
  };

  return (
    <FormProvider {...form}>
      <>
        <form 
          onSubmit={form.handleSubmit(onSubmit)}
          className="flex flex-col gap-[1em] mb-[4em]"
        >
          <BasicInfoSection
            register={form.register}
            errors={form.formState.errors}
            setError={form.setError}
            clearErrors={form.clearErrors}
            setValue={form.setValue}
            watch={form.watch}
          />     

          {/* TODO: Put up explanation for the user to understand what this is */}
          {/* TODO: There was a bug which I have forgotten */}
          {/* TODO: Dont like the leading zero */}
          <PricingSection
            register={form.register} 
            errors={form.formState.errors}
            setValue={form.setValue}
            watch={form.watch}
          />

          <InventorySection
            register={form.register}
            errors={form.formState.errors}
            setValue={form.setValue}
            watch={form.watch}
          />

          <VariationSection 
            ref={variantRef} 
            isHydrating={isHydrating} 
          />
        </form>

        {/* Save changes prompt */}
        {/* {showPrompt && (
          <div className="fixed inset-0 bg-[#000}/40 z-50 flex items-center justify-center">
            <div className="bg-white p-6 rounded-md shadow-md max-w-sm w-full">
              <p className="text-gray-800 mb-4 text-sm">
                You have unsaved changes. Are you sure you want to leave this page?
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={cancelNavigation}
                  className="text-sm px-4 py-2 rounded border border-gray-300 hover:bg-gray-100"
                >
                  Stay
                </button>
                <button
                  onClick={confirmNavigation}
                  className="text-sm px-4 py-2 rounded bg-[#000] text-white hover:bg-gray-800"
                >
                  Leave
                </button>
              </div>
            </div>
          </div>
        )} */}
      </>
    </FormProvider>
  );
};

export default ProductPageForm;

